/**
 * WindowPartitionPicker — the PARTITION BY sub-editor of `WindowSpecEditor`:
 * one removable chip per partition expression plus a schema-driven
 * `[+ add]` column chooser (reuses `CommandPickerPopover`, the same picker
 * `InsertColumnMap` uses). Closes the "opaque OVER text box" part of
 * ANA-14/16/20/22 for the partition clause.
 *
 * Controlled: emits the next `partitionBy` array; the parent (`WindowSpecEditor`)
 * serializes + commits via `modifySelectWindow`.
 */
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover, type CommandPickerItem } from '../common/CommandPickerPopover';
import { RemoveButton } from '../common/RemoveButton';

export interface WindowPartitionPickerProps {
  readonly partitionBy: readonly string[];
  readonly onChange: (next: readonly string[]) => void;
  readonly testIdPrefix: string;
  /** Schema-driven column suggestions for the `[+ add]` chooser. */
  readonly columnItems: readonly CommandPickerItem[];
  /** Resolve a picked item key back to the expression text to append. */
  readonly resolveExpr: (key: string) => string | undefined;
  readonly loading: boolean;
}

export function WindowPartitionPicker({
  partitionBy,
  onChange,
  testIdPrefix,
  columnItems,
  resolveExpr,
  loading,
}: WindowPartitionPickerProps) {
  const prefix = `${testIdPrefix}-partition`;

  const addColumn = (key: string): void => {
    const expr = resolveExpr(key);
    if (!expr) return;
    onChange([...partitionBy, expr]);
  };

  const removeAt = (index: number): void => {
    onChange(partitionBy.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${prefix}-row`}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Partition by</span>
      {partitionBy.length === 0 ? (
        <span className="text-[11px] italic text-muted-foreground" data-testid={`${prefix}-empty`}>
          {vqeCopy.window.noPartition}
        </span>
      ) : (
        partitionBy.map((expr, index) => (
          <span
            key={`${expr}-${index}`}
            className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px]"
            data-testid={`${prefix}-chip-${index}`}
          >
            {expr}
            <RemoveButton
              testId={`${prefix}-remove-${index}`}
              ariaLabel={`Remove partition ${expr}`}
              disabled={false}
              onClick={() => removeAt(index)}
              className="h-4 w-4"
            />
          </span>
        ))
      )}
      <CommandPickerPopover
        testId={`${prefix}-add`}
        triggerLabel={vqeCopy.actions.addColumnShort}
        disabled={false}
        tooltip={vqeCopy.window.addPartition}
        items={columnItems}
        onSelect={addColumn}
        loading={loading}
        emptyMessage={vqeCopy.window.noColumnsInScope}
      />
    </div>
  );
}
